import { Classes, Panel, PanelStack2 } from '@blueprintjs/core';
import classNames from 'classnames';
import omit from 'lodash/omit';
import * as React from 'react';
import { Dialog } from 'vet-bones/components';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { updateMappingsForResourceFields } from 'src/containers/ProjectDiagram/utils/resource/updateMappingsForResourceFields';
import { updateResourceDataSourceType } from 'src/containers/ProjectDiagram/utils/resource/updateResourceDataSourceType';
import { updateResourceName } from 'src/containers/ProjectDiagram/utils/resource/updateResourceName';
import { ProjectResourcePreviewDialogFooter } from 'src/containers/ProjectResourceDialog/ProjectResourcePreviewDialogFooter';
import { ProjectResourceDialogFooterProps } from 'src/containers/ProjectResourceDialog/types';
import { useIsVgDirty } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useIsVgDirty';
import { useLocalVirtualGraphOptions } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphOptionsValue';
import { useLocalVirtualGraph } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphValue';
import { ProjectVirtualGraphPreviewDialogContextProvider } from 'src/containers/ProjectVirtualGraphPreviewDialog/ProjectVirtualGraphPreviewDialogContextProvider';
import {
  ProjectVirtualGraphPreviewDialogOverviewPanel,
  ProjectVirtualGraphPreviewDialogOverviewPanelProps,
} from 'src/containers/ProjectVirtualGraphPreviewDialog/ProjectVirtualGraphPreviewDialogOverviewPanel';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectVirtualGraphPreviewDialogBodyProps = {
  handleToggleDialog: (isOpen: boolean) => void;
  virtualGraph: ProjectVirtualGraph;
};

export const ProjectVirtualGraphPreviewDialogBody: React.VFC<
  ProjectVirtualGraphPreviewDialogBodyProps
> = ({ handleToggleDialog, virtualGraph }) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const [localVirtualGraph] = useLocalVirtualGraph();
  const [localVirtualGraphOptions] = useLocalVirtualGraphOptions();

  const [errorText, setErrorText] = React.useState('');
  const [footerProps, setFooterProps] =
    React.useState<ProjectResourceDialogFooterProps>({});

  const isDirty = useIsVgDirty(
    virtualGraph,
    localVirtualGraph,
    localVirtualGraphOptions
  );

  const handleSave = React.useCallback(() => {
    if (errorText || !localVirtualGraph || !localVirtualGraphOptions) {
      return;
    }

    const virtualGraphToSave: ProjectVirtualGraph = {
      ...virtualGraph,
      ...omit(localVirtualGraph, 'errors', 'isSettingsOpenKey'),
      options: localVirtualGraphOptions,
    };
    dispatch(
      projectsActionCreators.updatedVirtualGraphForProject(
        projectId,
        virtualGraphToSave
      )
    );

    if (diagram) {
      commitDiagramModel(diagram, (model) => {
        if (localVirtualGraph.name !== virtualGraph.name) {
          updateResourceName(virtualGraph.id, localVirtualGraph.name)(model);
        }

        const resourceKey = getResourceKey(virtualGraph.id);
        const areFieldReferencesKept =
          typeof localVirtualGraph.from === 'object' &&
          typeof virtualGraph.from === 'object' &&
          // we assume that columns are kept the same for the same table, unless the number changes
          // (handled in updateMappingsForResourceFields). Might not handle the very rare case where
          // someone shuffles columns for some reason)
          localVirtualGraph.from.name === virtualGraph.from.name;

        if (localVirtualGraph.dataSourceType) {
          updateResourceDataSourceType(
            resourceKey,
            localVirtualGraph.dataSourceType
          )(model);
        }
        updateMappingsForResourceFields({
          areFieldReferencesKept,
          newFields: localVirtualGraph.fields,
          oldFields: virtualGraph.fields,
          resourceKey,
        })(model);
      });

      diagram.undoManager.clear();
    }

    handleToggleDialog(false);
  }, [
    diagram,
    dispatch,
    errorText,
    handleToggleDialog,
    localVirtualGraph,
    localVirtualGraphOptions,
    projectId,
    virtualGraph,
  ]);

  const initialPanel: Panel<ProjectVirtualGraphPreviewDialogOverviewPanelProps> =
    {
      props: {
        setErrorText,
        setFooterProps,
        virtualGraph,
      },
      renderPanel: ProjectVirtualGraphPreviewDialogOverviewPanel,
    };

  return (
    <>
      <div className={Classes.DIALOG_BODY}>
        <PanelStack2
          // type bug when using PanelStack in uncontrolled mode (using initialPanel)
          // https://github.com/palantir/blueprint/issues/5359
          initialPanel={initialPanel as any}
          renderActivePanelOnly
          showPanelHeader={false}
        />
      </div>
      <ProjectResourcePreviewDialogFooter
        backButtonText={
          footerProps.handleBackButtonClick
            ? copy.components.projectResourceDialog.backButton
            : copy.components.projectResourceDialog.cancelButton
        }
        // weird looking ternary here, but we have to ensure that error
        // text doesn't show for the wrong buttons on different parts of
        // the dialog (ex: changing data source should not be gated by
        // errors for editing virtual graph resource)
        isSubmitDisabled={
          footerProps.handleCreateButtonClick
            ? Boolean(footerProps.createButtonsError)
            : !isDirty || Boolean(errorText)
        }
        onBack={
          footerProps.handleBackButtonClick || (() => handleToggleDialog(false))
        }
        onSubmit={footerProps.handleCreateButtonClick || handleSave}
        submitButtonText={
          footerProps.handleCreateButtonClick
            ? copy.components.projectResourceDialog.createButton
            : copy.components.projectResourceDialog.saveButton
        }
        submitErrorText={
          footerProps.handleCreateButtonClick
            ? footerProps.createButtonsError
            : errorText
        }
      />
    </>
  );
};

export const ProjectVirtualGraphPreviewDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const { resourceId } = useReduxSelector(
    (state) => state.ui.virtualGraphPreviewDialog
  );
  const virtualGraph = useReduxSelector(
    projectVirtualGraphSelector(projectId, resourceId)
  );
  const { isOpen } = useReduxSelector(
    (state) => state.ui.virtualGraphPreviewDialog
  );

  const handleToggleDialog = React.useCallback(
    (isOpen: boolean) =>
      dispatch(uiSlice.actions.toggledVirtualGraphPreviewDialog({ isOpen })),
    [dispatch]
  );

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-project-resource', 'sd-dialog-large')}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={() => handleToggleDialog(false)}
      title={copy.components.virtualGraphPreviewDialog.title}
    >
      {virtualGraph ? (
        <ProjectVirtualGraphPreviewDialogContextProvider
          virtualGraph={virtualGraph}
        >
          <ProjectVirtualGraphPreviewDialogBody
            handleToggleDialog={handleToggleDialog}
            virtualGraph={virtualGraph}
          />
        </ProjectVirtualGraphPreviewDialogContextProvider>
      ) : null}
    </Dialog>
  );
};
